import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { BookOpen, Lightbulb, Clapperboard, Sparkles } from "lucide-react";

const TABS = [
  { key: "entries", label: "Knowledge Base", icon: BookOpen },
  { key: "lessons", label: "Lessons Learned", icon: Lightbulb },
  { key: "creative-refs", label: "Creative Refs", icon: Clapperboard },
  { key: "prompts", label: "AI Prompts", icon: Sparkles },
];

const FIELD_BY_SECTION = {
  entries: { body: "insight", sub: "what_happened" },
  lessons: { body: "lesson", sub: "what_happened" },
  "creative-refs": { body: "why_saved", sub: "use_case" },
  prompts: { body: "prompt", sub: "purpose" },
};

export default function Knowledge() {
  const [tab, setTab] = useState("entries");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/v1/knowledge/").then((r) => setCounts(r.data.counts || {})).catch((e) => {
      if (e?.response?.status === 403) setDenied(true);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/v1/knowledge/${tab}`).then((r) => setItems(r.data.items || [])).catch((e) => {
      if (e?.response?.status === 403) setDenied(true);
    }).finally(() => setLoading(false));
  }, [tab]);

  const runSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) { setSearchResults(null); return; }
    try {
      const r = await api.get(`/v1/knowledge/search/all?q=${encodeURIComponent(q)}`);
      setSearchResults(r.data.items || []);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
    }
  };

  const fields = FIELD_BY_SECTION[tab];
  const list = searchResults ?? items;

  if (denied) {
    return (
      <div className="p-8" data-testid="knowledge-access-denied">
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center">
          <h1 className="font-editorial text-2xl tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-[#57534E]">Your role doesn't have permission to view the Knowledge Explorer.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="knowledge-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-editorial text-4xl tracking-tight">Knowledge Explorer</h1>
          <p className="text-sm text-[#57534E] mt-1">Institutional memory — lessons, references, and prompts. Read-only.</p>
        </div>
        <form onSubmit={runSearch} className="flex items-center gap-2">
          <input
            data-testid="knowledge-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search all knowledge…"
            className="w-72 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50"
          />
          <button type="submit" data-testid="knowledge-search-submit" className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817]">Search</button>
          {searchResults && (
            <button type="button" data-testid="knowledge-search-clear" onClick={() => { setSearchResults(null); setQ(""); }} className="text-xs text-[#A8A29E] hover:text-[#1C1C1E]">Clear</button>
          )}
        </form>
      </div>

      {!searchResults && (
        <div className="flex gap-2 border-b border-[#E7E5E4]">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`knowledge-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                  tab === t.key ? "border-[#FFC817] text-[#B45309]" : "border-transparent text-[#57534E] hover:text-[#1C1C1E]"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
                <span className="ml-1 rounded-full bg-[#E7E5E4] px-1.5 py-0.5 text-[10px] text-[#57534E]">{counts[t.key] ?? "—"}</span>
              </button>
            );
          })}
        </div>
      )}

      {searchResults && (
        <div className="text-xs text-[#A8A29E]" data-testid="knowledge-search-summary">{searchResults.length} result(s) for "{q}"</div>
      )}

      {loading && !searchResults ? (
        <div className="text-sm text-[#A8A29E]">Loading…</div>
      ) : list.length === 0 ? (
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center text-sm text-[#A8A29E]" data-testid="knowledge-empty">
          Nothing here yet — populate the Notion database to see it surface here.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((item) => {
            const f = FIELD_BY_SECTION[tab] || fields || { body: "insight", sub: "" };
            return (
              <div key={item.id} data-testid={`knowledge-item-${item.id}`} className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-4 hover:border-[#D6D3D1]">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm text-[#1C1C1E] font-medium">{item.name}</div>
                    <p className="text-xs text-[#57534E] mt-1.5 leading-relaxed">{item[f.body] || item[f.sub] || "—"}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {(item.category || item.type) && (
                        <span className="rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#57534E]">{item.category || item.type}</span>
                      )}
                      {(item.client_names || []).map((n) => (
                        <span key={n} className="rounded-full bg-[#FFC817]/10 border border-[#FFC817]/30 px-2 py-0.5 text-[10px] text-[#B45309]">{n}</span>
                      ))}
                      {(item.project_names || []).map((n) => (
                        <span key={n} className="rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] text-[#57534E]">{n}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-[#A8A29E] flex-shrink-0">{item.date?.slice(0, 10)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
